import "./FinancePage.scss";

import { FinanceFilterBar } from "components/Header/FinanceFilter/FinanceFilterContainer";
import LoadingIcon from "components/Loading/LoadingIcon";
import GroupedQueryBuilder from "pages/finance/filters/components/GroupedQueryBuilder/GroupedQueryBuilder";
import { useFinanceFilterData } from "pages/finance/filters/hooks/useFinanceFilterData";
import { useFinanceFilterState } from "pages/finance/filters/hooks/useFinanceFilterState";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import BackgroundJobsDrawer from "./components/BackgroundJobsDrawer";
import ReportTable from "./components/ReportTable";
import { useReportActions } from "./contexts/ReportActionsContext";
import { useReportOperations } from "./hooks/useReportOperations";

// main finance reporting page with filter bar, action buttons in header, and report table
// uses Context with proper memoization to prevent infinite loops:
// - stores primitives and serialized data in Context (stable comparisons)
// - uses refs for mutable objects (filters, reportData, selectedRows) to avoid stale closures
// - only updates Context when primitive values actually change
const FinancePage = () => {
  const { setState: setReportActionsState } = useReportActions();
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isRulesExpanded, setIsRulesExpanded] = useState(false);
  const [lastRunTime, setLastRunTime] = useState<Date | null>(null);
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // use filter state hooks
  const { filters, setFilter, removeFilter, clearFilters, updateFilters } =
    useFinanceFilterState();

  // ensure default report type is in URL on initial load
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const reportTypeParam = searchParams.get("reportType");

    // if no report type in URL, add INVOICE_LIST as default
    if (!reportTypeParam) {
      searchParams.set("reportType", "INVOICE_LIST");
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location.pathname, location.search, navigate]);

  // get filter data for dropdowns
  const {
    clientOptions,
    fileOptions,
    invoiceOptions,
    isClientsFetching,
    isFilesFetching,
    isInvoicesFetching,
    debouncedSetClientSearchTerm,
    debouncedSetFileSearchTerm,
    debouncedSetInvoiceSearchTerm,
  } = useFinanceFilterData();

  // memoize watchedReportType to prevent unnecessary re-renders
  const watchedReportType = useMemo(
    () => filters.report_type,
    [filters.report_type?.value],
  );

  // determine if rules should be shown for this report type
  const shouldShowRules = useMemo(() => {
    const reportType = watchedReportType?.value?.toString();
    return (
      reportType === "INVOICE_LINE_ITEM_REPORT" ||
      reportType === "WORK_IN_PROGRESS"
    );
  }, [watchedReportType?.value]);

  // URL is already managed by useFinanceFilterState, so this is a no-op
  const updateUrlWithCurrentFilters = () => {
    // filters are already synced to URL by useFinanceFilterState
  };

  const {
    reportState: {
      reportData,
      reportColumns,
      showReportTable,
      page,
      totalRecords,
      selectedRows,
      setSelectedRows,
      reportMeta,
      sortColumns,
    },
    operations: {
      onGenerateReport,
      onGenerateLedesReport,
      onViewReportData,
      handlePageChange,
      handleSortChange,
      resetReportState,
    },
    loading: { isGenerating, isLoadingReportData },
    constants: { FINANCE_PAGE_SIZE },
  } = useReportOperations({
    urlStateManager: null, // no longer using urlStateManager
    updateUrlWithCurrentFilters,
  });

  // track previous report type to detect actual changes
  const prevReportTypeRef = useRef<string | number | boolean | undefined>();

  // clear report table when report type changes
  useEffect(() => {
    const currentReportType = watchedReportType?.value;

    // only reset if report type actually changed (not on initial load)
    if (
      prevReportTypeRef.current &&
      prevReportTypeRef.current !== currentReportType
    ) {
      resetReportState();
      setIsSelectAllMode(false);
    }

    prevReportTypeRef.current = currentReportType;
  }, [watchedReportType?.value, resetReportState]);

  // stable callback handlers - use filtersRef to get current filters without causing loops
  const handleViewReport = useCallback(async () => {
    setIsSelectAllMode(false);
    setSelectedRows(new Set());
    await onViewReportData(filtersRef.current, 1);
    setLastRunTime(new Date());
  }, [onViewReportData]);

  const handleGenerateReport = useCallback(async () => {
    await onGenerateReport(filtersRef.current);
  }, [onGenerateReport]);

  const handleGenerateLedesReport = useCallback(async () => {
    await onGenerateLedesReport(filtersRef.current);
  }, [onGenerateLedesReport]);

  const handleShareReport = useCallback(() => {
    const shareableUrl = window.location.href;
    navigator.clipboard.writeText(shareableUrl);
    // could show toast here if needed
  }, []);

  const handleResetReport = useCallback(() => {
    clearFilters();
    resetReportState();
    setLastRunTime(null);
  }, [clearFilters, resetReportState]);

  // use refs to track current values without triggering loops
  const reportDataRef = useRef(reportData);
  const selectedRowsRef = useRef(selectedRows);
  const filtersRef = useRef(filters);

  // store callback refs (updated every render but don't cause re-renders)
  const callbacksRef = useRef({
    onViewReport: handleViewReport,
    onGenerateReport: handleGenerateReport,
    onGenerateLedesReport: handleGenerateLedesReport,
    onShareReport: handleShareReport,
    onResetReport: handleResetReport,
    setSelectedRows,
  });

  useEffect(() => {
    reportDataRef.current = reportData;
    selectedRowsRef.current = selectedRows;
    filtersRef.current = filters;
    callbacksRef.current = {
      onViewReport: handleViewReport,
      onGenerateReport: handleGenerateReport,
      onGenerateLedesReport: handleGenerateLedesReport,
      onShareReport: handleShareReport,
      onResetReport: handleResetReport,
      setSelectedRows,
    };
  });

  // update Context with current state
  // only triggers when PRIMITIVE values change, not callbacks or object references
  useEffect(() => {
    const reportDataJson = reportDataRef.current
      ? JSON.stringify(reportDataRef.current)
      : null;
    const reportMetaJson = reportMeta ? JSON.stringify(reportMeta) : null;
    const selectedRowsArray = Array.from(selectedRowsRef.current);

    setReportActionsState({
      reportType: watchedReportType?.value?.toString() || null,
      isGenerating,
      isLoadingReportData,
      showReportTable,
      page,
      selectedRowsCount: selectedRows.size,
      canShowBatchActions:
        (watchedReportType?.value === "INVOICE_LIST" ||
          watchedReportType?.value === "SECTION_111_INVOICING") &&
        showReportTable,
      lastRunTime: lastRunTime,
      totalRecords: totalRecords || null,
      reportDataJson,
      reportMetaJson,
      selectedRowsArray,
      // callbacks from ref (always current, never trigger updates)
      ...callbacksRef.current,
      // Section 111 select-all mode
      isSelectAllMode,
      setIsSelectAllMode,
    });
  }, [
    setReportActionsState,
    watchedReportType?.value,
    isGenerating,
    isLoadingReportData,
    showReportTable,
    page,
    selectedRows.size,
    lastRunTime,
    reportMeta,
    isSelectAllMode,
    // NOT including callbacks - they're always current via ref
  ]);

  const handlePageChangeWithUrl = async (newPage: number) => {
    await handlePageChange(newPage, filtersRef.current);
  };

  // handler for removing filters with special date range logic
  const handleRemoveFilter = (key: keyof typeof filters) => {
    if (key === "posted_date_start" || key === "posted_date_end") {
      updateFilters({
        posted_date_start: null,
        posted_date_end: null,
      });
    } else if (key === "service_date_from" || key === "service_date_to") {
      updateFilters({
        service_date_from: null,
        service_date_to: null,
      });
    } else if (key === "service_created_from" || key === "service_created_to") {
      updateFilters({
        service_created_from: null,
        service_created_to: null,
      });
    } else if (key === "file_opened_on_from" || key === "file_opened_on_to") {
      updateFilters({
        file_opened_on_from: null,
        file_opened_on_to: null,
      });
    } else if (key === "send_date_from" || key === "send_date_to") {
      updateFilters({
        send_date_from: null,
        send_date_to: null,
      });
    } else if (key === "doi_from" || key === "doi_to") {
      updateFilters({
        doi_from: null,
        doi_to: null,
      });
    } else if (key === "tpoc_from" || key === "tpoc_to") {
      updateFilters({
        tpoc_from: null,
        tpoc_to: null,
      });
    } else if (key === "medicare_from" || key === "medicare_to") {
      updateFilters({
        medicare_from: null,
        medicare_to: null,
      });
    } else {
      removeFilter(key);
    }
  };

  return (
    <div className="page-container">
      <div className="page-content">
        <div className="FinancePage">
          {/* filter bar in action container */}
          <div className="report-actions-container">
            <FinanceFilterBar
              filters={filters}
              onRemoveFilter={handleRemoveFilter}
              onClearFilters={clearFilters}
              isDropdownOpen={isFilterDropdownOpen}
              onToggleDropdown={() =>
                setIsFilterDropdownOpen(!isFilterDropdownOpen)
              }
              onUpdateFilter={(key, value) => setFilter(key, value)}
              onUpdateFilters={updateFilters}
              clientOptions={clientOptions}
              fileOptions={fileOptions}
              invoiceOptions={invoiceOptions}
              isClientsFetching={isClientsFetching}
              isFilesFetching={isFilesFetching}
              isInvoicesFetching={isInvoicesFetching}
              onClientSearch={debouncedSetClientSearchTerm}
              onFileSearch={debouncedSetFileSearchTerm}
              onInvoiceSearch={debouncedSetInvoiceSearchTerm}
              isRulesExpanded={isRulesExpanded}
              onToggleRules={() => setIsRulesExpanded(!isRulesExpanded)}
              showRules={shouldShowRules}
            />

            {/* rules section - expands below filter bar */}
            {shouldShowRules && isRulesExpanded && (
              <div className="rules-expanded-section">
                <div className="rules-expanded-content">
                  <div className="rules-header">
                    <span className="material-icons">rule</span>
                    <h3>Advanced Filtering Rules</h3>
                  </div>
                  <p className="rules-description">
                    Define custom filtering rules for clients and service types.
                    Rules are applied when generating reports.
                  </p>
                  <div className="rules-builder-wrapper">
                    <GroupedQueryBuilder
                      value={filters.client_service_map}
                      onChange={(value) =>
                        setFilter("client_service_map", value)
                      }
                      disabled={false}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* report table display - fixed to viewport */}
          {!showReportTable && isLoadingReportData ? (
            <div className="report-loading-state">
              <LoadingIcon
                size="large"
                showText={true}
                message="Loading Report..."
              />
            </div>
          ) : !showReportTable ? (
            <div className="report-empty-state">
              <div className="no-tasks">
                <div className="no-tasks-header">No Report Generated</div>
                <div className="no-tasks-icons">
                  <div>
                    <span className="material-icons">assessment</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ReportTable
              showReportTable={showReportTable}
              reportColumns={reportColumns}
              reportData={reportData}
              watchedReportType={watchedReportType}
              selectedRows={selectedRows}
              onSelectedRowsChange={setSelectedRows}
              isLoadingReportData={isLoadingReportData}
              page={page}
              pageSize={FINANCE_PAGE_SIZE}
              totalRecords={totalRecords}
              onPageChange={handlePageChangeWithUrl}
              sortColumns={sortColumns}
              onSortChange={(newSortColumns) =>
                handleSortChange(newSortColumns, filtersRef.current)
              }
              reportMeta={reportMeta}
              isSelectAllMode={isSelectAllMode}
              onSelectAllMode={setIsSelectAllMode}
            />
          )}
        </div>
      </div>
      <BackgroundJobsDrawer />
    </div>
  );
};

export default FinancePage;
